/* ═══════════════════════════════════════════════════════════
   tessara-cart.js — Cart mutations: add, update, remove.
   Single source of truth for client-side cart state.
   Uses Section Rendering API to refresh cart drawer HTML.
   ═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── State ── */
  let cartMutating = false;  // race condition guard

  /* ── Helpers ── */
  async function cartPost(endpoint, body) {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let errBody = '';
      try {
        const j = await res.json();
        errBody = j.description || j.message || (j.errors ? JSON.stringify(j.errors) : '') || JSON.stringify(j);
      } catch {}
      throw new Error(`${res.status}: ${errBody}`);
    }
    return res.json();
  }

  async function refreshCartDrawer() {
    const drawerEl   = document.getElementById('cart-drawer');
    const drawerBody = document.getElementById('cart-drawer-body');
    try {
      const res = await fetch('/?sections=cart-drawer');
      if (!res.ok) throw new Error('section-render-fail');
      const data = await res.json();
      const html = data['cart-drawer'];
      if (html) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        // Refresh body
        const newBody = tmp.querySelector('#cart-drawer-body');
        if (drawerBody && newBody) drawerBody.innerHTML = newBody.innerHTML;
        // Refresh footer (subtotal + shipping bar)
        const newFooter = tmp.querySelector('.tds-cart-drawer__footer');
        const oldFooter = drawerEl?.querySelector('.tds-cart-drawer__footer');
        if (oldFooter && newFooter) {
          oldFooter.outerHTML = newFooter.outerHTML;
        } else if (oldFooter && !newFooter) {
          oldFooter.remove();
        } else if (!oldFooter && newFooter && drawerEl) {
          drawerEl.appendChild(newFooter);
        }
        // Update title count
        const newTitle = tmp.querySelector('.tds-cart-drawer__title');
        const oldTitle = drawerEl?.querySelector('.tds-cart-drawer__title');
        if (oldTitle && newTitle) oldTitle.innerHTML = newTitle.innerHTML;
      }
    } catch {
      if (drawerBody) {
        drawerBody.innerHTML = '<p style="color:var(--text-error);padding:var(--space-4);">Unable to load cart. Please refresh.</p>';
      }
    }
    bindCartItemEvents();
  }

  async function refreshCartBadge(announce = false) {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      const badge = document.querySelector('[data-cart-count]');
      if (badge) {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      }
      const cartBtn = document.querySelector('[data-action="open-cart"]');
      if (cartBtn) {
        const label = cart.item_count === 0
          ? 'Open cart'
          : `Open cart, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        cartBtn.setAttribute('aria-label', label);
      }
      if (announce) {
        const liveRegion = document.getElementById('cart-live-region');
        if (liveRegion && cart.item_count > 0) {
          liveRegion.textContent = `Cart updated: ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        }
      }
    } catch { /* silent — badge is cosmetic */ }
  }

  window.tessaraCart = {
    refreshBadge: () => refreshCartBadge(false),
    refresh: () => Promise.all([refreshCartBadge(true), refreshCartDrawer()]),
  };

  /* ════════════════════════════════════
     ATC — intercept submit on PDP
  ════════════════════════════════════ */
  function bindATC() {
    const form = document.getElementById('tds-atc-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (cartMutating) return;
      cartMutating = true;

      const btn   = form.querySelector('#tds-atc-btn');
      const label = btn?.querySelector('.tds-atc-btn__label');
      const spinner = btn?.querySelector('.tds-spinner');
      const msgEl = document.getElementById('tds-atc-msg');
      const variantId = parseInt(form.querySelector('#tds-variant-id')?.value || form.querySelector('[name=id]')?.value);
      const qty = parseInt(form.querySelector('[name=quantity]')?.value || 1);

      if (!variantId || isNaN(variantId)) {
        cartMutating = false;
        return;
      }

      // Loading state
      if (btn) btn.dataset.state = 'loading';
      if (label) label.hidden = true;
      if (spinner) spinner.hidden = false;
      if (msgEl) msgEl.textContent = '';

      // 10s timeout failsafe
      const timeout = setTimeout(() => {
        if (btn) btn.dataset.state = 'default';
        if (label) { label.hidden = false; label.textContent = 'Add to Cart'; }
        if (spinner) spinner.hidden = true;
        cartMutating = false;
      }, 10000);

      try {
        await cartPost('/cart/add.js', { id: variantId, quantity: qty });

        // Success
        clearTimeout(timeout);
        if (btn) btn.dataset.state = 'success';
        if (label) { label.hidden = false; label.textContent = 'Added to Cart'; }
        if (spinner) spinner.hidden = true;

        await Promise.all([refreshCartBadge(true), refreshCartDrawer()]);
        window.openCartDrawer?.();

        // Reset button after 3s
        setTimeout(() => {
          if (btn) btn.dataset.state = 'default';
          if (label) label.textContent = 'Add to Cart';
        }, 3000);

      } catch (err) {
        clearTimeout(timeout);
        if (btn) btn.dataset.state = 'default';
        if (label) { label.hidden = false; label.textContent = 'Add to Cart'; }
        if (spinner) spinner.hidden = true;
        if (msgEl) {
          // Extract Shopify's actual error so it's visible for diagnosis.
          // err.message format from cartPost: "422: The product '...' is already sold out."
          const raw = err?.message || '';
          const shopifyMsg = raw.replace(/^\d+:\s*/, '').trim();
          msgEl.textContent = shopifyMsg || 'Something went wrong. Please try again.';
          msgEl.className = 'tds-inline-msg tds-inline-msg--error';
        }
        console.error('[tessara-cart] ATC error:', err);
      } finally {
        cartMutating = false;
      }
    });
  }

  /* ════════════════════════════════════
     Cart item interactions (qty / remove)
  ════════════════════════════════════ */
  function bindCartItemEvents() {
    // Qty stepper buttons
    document.querySelectorAll('.tds-qty-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (cartMutating) return;
        const key    = btn.dataset.key;
        const action = btn.dataset.action;
        const input  = document.querySelector(`.tds-qty-input[data-key="${key}"]`);
        if (!input) return;

        let qty = parseInt(input.value);
        if (action === 'increase') qty += 1;
        if (action === 'decrease') qty = Math.max(1, qty - 1);
        input.value = qty;

        await updateCartItem(key, qty);
      });
    });

    // Qty input direct edit
    document.querySelectorAll('.tds-qty-input').forEach((input) => {
      input.addEventListener('change', async () => {
        const key = input.dataset.key;
        let qty = parseInt(input.value);
        if (isNaN(qty) || qty < 1) { qty = 1; input.value = 1; }
        await updateCartItem(key, qty);
      });
    });

    // Remove buttons
    document.querySelectorAll('.tds-cart-item__remove').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (cartMutating) return;
        const key = btn.dataset.key;
        await updateCartItem(key, 0);
      });
    });
  }

  async function updateCartItem(key, quantity) {
    if (cartMutating) return;
    cartMutating = true;
    try {
      await cartPost('/cart/change.js', { id: key, quantity });
      await Promise.all([refreshCartBadge(true), refreshCartDrawer()]);
    } catch { /* silent; UI already matches optimistic update */ }
    finally { cartMutating = false; }
  }

  /* ── Init ── */
  document.addEventListener('DOMContentLoaded', () => {
    bindATC();
    bindCartItemEvents();
    refreshCartBadge();
  });

  // Refresh drawer + badge when QV / set picker dispatches cart:refresh
  document.addEventListener('cart:refresh', () => {
    refreshCartBadge(true);
    refreshCartDrawer();
  });

  // Re-bind after Section Rendering API swaps content
  document.addEventListener('tessara:section-rendered', () => {
    bindATC();
    bindCartItemEvents();
  });

})();
